import { useEffect } from 'react';
import { PickerOption } from '@sdk/components/forms/types';
import getUserGeo from '@sdk/utils/api/getUserGeo';
import gettext from '@sdk/lang/gettext';

import { matchColumns } from '@web/locations/ImportLocations/MapColumns/mapColumns.utils';

const columnOptions: PickerOption[] = [
  {
    value: 'name',
    label: gettext('Location name'),
  },
  {
    value: 'address',
    label: gettext('Address'),
  },
  {
    value: 'externalId',
    label: gettext('External ID'),
  },
  {
    value: 'phone',
    label: gettext('Contact number'),
  },
  {
    value: 'contact',
    label: gettext('Contact name'),
  },
  {
    value: 'notes',
    label: gettext('Notes'),
  },
];

export const useMapColumns = ({
  parsedFileData,
  columnMap,
  setColumnMap,
  rowSelection,
  setRowSelection,
  pickedColumnsDict,
  defaultCountryCode,
  setDefaultCountryCode,
}: {
  parsedFileData: string[][];
  columnMap: Record<number, string>;
  setColumnMap: (value: Record<number, string>) => void;
  rowSelection: Record<number, boolean>;
  setRowSelection: (value: Record<number, boolean>) => void;
  pickedColumnsDict: Record<string, boolean>;
  defaultCountryCode: string;
  setDefaultCountryCode: (value: string) => void;
}) => {
  const isAllSelected = () => {
    return rowSelection && Object.values(rowSelection).every(Boolean);
  };

  const toggleAllSelected = () => {
    const currrentState = isAllSelected();

    const newRowSelectionState = Object.keys(rowSelection).reduce((agg, key) => {
      agg[key] = !currrentState;
      return agg;
    }, {});

    setRowSelection(newRowSelectionState);
  };

  useEffect(() => {
    let columnMap = parsedFileData[0].reduce((agg, value, index) => {
      agg[index] = null;
      return agg;
    }, {});

    let rowSelectionMap = parsedFileData.reduce((agg, value, index) => {
      agg[index] = true;
      return agg;
    }, {});

    [columnMap, rowSelectionMap] = matchColumns({
      columnOptions,
      columnMap,
      rowSelectionMap,
      firstRow: parsedFileData[0],
    });

    setColumnMap(columnMap);
    setRowSelection(rowSelectionMap);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedFileData]);

  useEffect(() => {
    const setCountryCode = async () => {
      const geo = await getUserGeo();
      setDefaultCountryCode(`+${geo.countryCode}:${geo.country}`);
    };

    if (pickedColumnsDict.phone && !defaultCountryCode) {
      setCountryCode();
    }
  }, [defaultCountryCode, setDefaultCountryCode, pickedColumnsDict]);

  const onRowSelectionChange = (rowIndex: number): void => {
    setRowSelection({
      ...rowSelection,
      [rowIndex]: !rowSelection[rowIndex],
    });
  };

  return {
    isAllSelected,
    toggleAllSelected,
    columnMap,
    setColumnMap,
    rowSelection,
    onRowSelectionChange,
    columnOptions,
  };
};
